import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";

// Mock data for demonstration
const mockData = {
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  visaPathway: {
    title: "Global Talent Visa",
    description: "For exceptional talent in science, engineering, humanities, and digital technology"
  },
  results: {
    overallScore: 85,
    criteria: {
      education: 90,
      experience: 85,
      skills: 80,
      language: 85,
      achievements: 85
    },
    matches: [{
      program: "Global Talent Visa",
      score: 85,
      requirements: {
        met: [
          "Educational Qualifications",
          "Professional Experience",
          "Technical Skills",
          "Industry Recognition"
        ],
        missing: [
          "Additional Leadership Experience",
          "International Publications"
        ]
      }
    }],
    recommendations: [
      "Consider pursuing leadership roles in technical projects",
      "Publish research or technical articles in international journals",
      "Obtain additional industry certifications",
      "Build a portfolio of innovative projects"
    ]
  }
};

const AssessmentOverview = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Transform criteria data for the chart
  const chartData = Object.entries(mockData.results.criteria).map(([category, score]) => ({
    category: category.charAt(0).toUpperCase() + category.slice(1),
    score,
    fill: "#8884d8"
  }));

  const handleDownload = async () => {
    setIsGenerating(true);
    try {
      const element = document.getElementById('assessment-overview');
      if (!element) return;

      const canvas = await html2canvas(element);
      const pdf = new jsPDF();
      
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = canvas.height * imgWidth / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save('assessment-report.pdf');
      
      toast({
        title: "Success",
        description: "Assessment report downloaded successfully",
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to generate PDF. Please try again.",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card id="assessment-overview">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Assessment Overview</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Last updated: {new Date(mockData.updated_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => navigate("/eligibility")}
            variant="outline"
          >
            New Assessment
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button 
            onClick={handleDownload} 
            disabled={isGenerating}
            className="bg-primary hover:bg-primary/90"
          >
            <Download className="mr-2 h-4 w-4" />
            {isGenerating ? "Generating..." : "Download Report"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Visa Pathway */}
        <div className="bg-accent/5 p-4 rounded-lg">
          <h3 className="font-medium mb-2">Selected Visa Pathway</h3>
          <p className="text-lg font-semibold">{mockData.visaPathway.title}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {mockData.visaPathway.description}
          </p>
        </div>

        {/* Chart */}
        <div className="h-[300px] mt-6">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="category" 
                tick={{ fill: '#FFFFFF' }}
                axisLine={{ stroke: '#FFFFFF' }}
              />
              <YAxis
                tick={{ fill: '#FFFFFF' }}
                axisLine={{ stroke: '#FFFFFF' }}
                domain={[0, 100]}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1A1B1E',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                }}
                labelStyle={{ color: '#FFFFFF' }}
                itemStyle={{ color: '#FFFFFF' }}
              />
              <Legend />
              <Bar
                dataKey="score"
                fill="#8884d8"
                radius={[4, 4, 0, 0]}
                name="Score (%)"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-accent/5 p-4 rounded-lg text-center">
            <div className="text-3xl font-bold mb-1">{mockData.results.overallScore}%</div>
            <div className="text-sm text-muted-foreground">Overall Score</div>
          </div>
          <div className="bg-accent/5 p-4 rounded-lg text-center">
            <div className="text-3xl font-bold mb-1">
              {mockData.results.matches[0].requirements.met.length}
            </div>
            <div className="text-sm text-muted-foreground">Criteria Met</div>
          </div>
          <div className="bg-accent/5 p-4 rounded-lg text-center">
            <div className="text-3xl font-bold mb-1">
              {mockData.results.matches[0].requirements.missing.length}
            </div>
            <div className="text-sm text-muted-foreground">Areas for Improvement</div>
          </div>
        </div>

        {/* Requirements */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <h3 className="font-medium">Requirements Met</h3>
            <ul className="space-y-2">
              {mockData.results.matches[0].requirements.met.map((req, index) => (
                <li key={index} className="flex items-center gap-2 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  {req}
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-3">
            <h3 className="font-medium">Areas for Improvement</h3>
            <ul className="space-y-2">
              {mockData.results.matches[0].requirements.missing.map((req, index) => (
                <li key={index} className="flex items-center gap-2 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
                  {req}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AssessmentOverview;